<template>
  <PrimeVueSlider
    v-model="sliderValues"
    :name="name"
    :min="Number(min)"
    :max="Number(max)"
    :step="step"
    :disabled="disabled || !(possibleValues.length > 1)"
    :aria-label="label"
    :aria-valuenow="sliderValues"
    :aria-valuemin="min + suffix"
    :aria-valuemax="max + suffix"
    class="mb-4"
    range
    :invalid="!isValidRange(sliderValues[0], sliderValues[1])"
    @change="userInput = true"
  />
  <div class="d-flex flex-row mt-2 gap-2">
    <PrimeVueFloatLabel variant="on">
      <PrimeVueInputNumber
        v-if="currencyFormatter"
        v-model.number="sliderValues[0]"
        :input-id="name + '_min'"
        fluid
        size="small"
        show-buttons
        :min="Number(min)"
        :step="step"
        :disabled="disabled || !(possibleValues.length > 1)"
        mode="currency"
        :min-fraction-digits="0"
        :currency="currency"
        :locale="locale"
        :invalid="!isValidRange(sliderValues[0], sliderValues[1])"
        @input="userInput = true"
      />
      <PrimeVueInputNumber
        v-else
        v-model.number="sliderValues[0]"
        :input-id="name + '_min'"
        fluid
        size="small"
        show-buttons
        :suffix="suffix"
        :min="Number(min)"
        :step="step"
        :disabled="disabled || !(possibleValues.length > 1)"
        :min-fraction-digits="0"
        :invalid="!isValidRange(sliderValues[0], sliderValues[1])"
        @input="userInput = true"
      />
      <label :for="name + '_min'">{{ minLabel }}</label>
    </PrimeVueFloatLabel>
    <PrimeVueFloatLabel variant="on">
      <PrimeVueInputNumber
        v-if="currencyFormatter"
        v-model.number="sliderValues[1]"
        :input-id="name + '_max'"
        fluid
        size="small"
        show-buttons
        :max="Number(max)"
        :step="step"
        :disabled="disabled || !(possibleValues.length > 1)"
        mode="currency"
        :min-fraction-digits="0"
        :currency="currency"
        :locale="locale"
        :invalid="!isValidRange(sliderValues[0], sliderValues[1])"
        @input="userInput = true"
      />
      <PrimeVueInputNumber
        v-else
        v-model.number="sliderValues[1]"
        :input-id="name + '_max'"
        fluid
        size="small"
        show-buttons
        :suffix="suffix"
        :max="Number(max)"
        :step="step"
        :disabled="disabled || !(possibleValues.length > 1)"
        :min-fraction-digits="0"
        :invalid="!isValidRange(sliderValues[0], sliderValues[1])"
        @input="userInput = true"
      />
      <label :for="name + '_max'">{{ maxLabel }}</label>
    </PrimeVueFloatLabel>
  </div>
  <div
    v-if="!isValidRange(sliderValues[0], sliderValues[1])"
    class="rte-text invalid-range mt-1"
  >
    <small>
      {{
        $textDictionary['productsList.invalidFilterRange'] ??
        'Invalid filter range [productsList.invalidFilterRange].'
      }}
    </small>
  </div>
</template>

<script setup lang="ts">
import { useProductFilterStore } from '~/stores/productFilterStore/productFilterStore';

const store = useProductFilterStore();
const { $textDictionary } = useNuxtApp();
const userInput = ref(false);

type CurrencyInformation = {
  locale?: string;
  currency?: string;
};

type Props = {
  name: string;
  label: string;
  possibleValues?: number[];
  values?: string[];
  min?: number;
  max?: number;
  step?: number;
  minLabel: string;
  maxLabel: string;
  disabled?: boolean;
  currencyFormatter?: CurrencyInformation;
};

const props = withDefaults(defineProps<Props>(), {
  name: 'slider',
  label: 'label',
  min: 0,
  max: 1,
  possibleValues: () => [],
  values: () => [],
  step: 1,
  disabled: false,
  currencyFormatter: undefined
});

const sliderValues = ref();

sliderValues.value = [
  Number(props.values[0] ?? Number(props.min)),
  Number(props.values[1] ?? Number(props.max))
];

const currency = computed(() => {
  return props.currencyFormatter?.currency ?? 'EUR';
});

const locale = computed(() => {
  return props.currencyFormatter?.locale ?? 'en';
});

function isValidRange(min: number, max: number) {
  const hit = props.possibleValues.find(
    (value: Number) =>
      Number(value) >= sliderValues.value[0] &&
      Number(value) <= sliderValues.value[1]
  );
  return Number(min) <= Number(max) && hit !== undefined;
}

const emit = defineEmits<{
  change: [minVal: number, maxValue: number];
}>();

const debouncedEmit = debounce(emit, 400);

const suffix = computed(() => {
  return $textDictionary.value[`productsList.filter.tags.${props.name}.label`]
    ? ' ' +
        $textDictionary.value[`productsList.filter.tags.${props.name}.label`]
    : '';
});

function onChangeSlider(min: number, max: number) {
  if (isValidRange(min, max) && userInput.value) {
    // @ts-ignore
    debouncedEmit('change', min, max);
  }
}

function setSliderValues() {
  if (store.activeFilterCount === 0) {
    sliderValues.value[0] = Number(props.min);
    sliderValues.value[1] = Number(props.max);
  } else {
    if (props.min > sliderValues.value[0]) {
      sliderValues.value[0] = Number(props.min);
    }
    if (sliderValues.value[1] > props.max) {
      sliderValues.value[1] = Number(props.max);
    }
  }
}

watch(
  () => props.min,
  () => {
    setSliderValues();
  }
);

watch(
  () => props.max,
  () => {
    setSliderValues();
  }
);
watch(
  () => store.activeFilterCount,
  () => {
    if (store.activeFilterCount === 0) {
      setSliderValues();
      userInput.value = false;
    }
  }
);

watch(
  () => sliderValues.value[0],
  () => {
    onChangeSlider(sliderValues.value[0], sliderValues.value[1]);
  }
);
watch(
  () => sliderValues.value[1],
  () => {
    onChangeSlider(sliderValues.value[0], sliderValues.value[1]);
  }
);
</script>

<style>
.invalid-range {
  color: #ff5252;
}
</style>
